import React, { useEffect, useState } from 'react'
import { CalendarRange } from 'lucide-react'
import { RANGE_PRESETS } from './model'
import { isCustomRange, nyToday, rangeProblem } from '../../utils/dateRange'

// The staff screens' range control. The dates a preset resolves to come
// back from the server in `range`, in New York days, so this never computes
// them itself. Each screen passes its own `presets` (the Home has MTD, not
// YTD).
//
// `value` is a preset id or a custom `{ from, to }`, and `onChange` gets the
// same. With `allowCustom`, a "Custom" button opens From/To date inputs; a
// custom range is only handed up once both ends are real dates, in order,
// and not after today in New York. Half-typed input stays local, so editing
// one side never fires a request for a range nobody meant.
export default function RangeSelect({ value, onChange, presets = RANGE_PRESETS, allowCustom = false }) {
  const custom = isCustomRange(value)
  const [open, setOpen] = useState(custom)
  const [draftFrom, setDraftFrom] = useState(custom ? value.from : '')
  const [draftTo, setDraftTo] = useState(custom ? value.to : '')
  const [error, setError] = useState(null)
  const today = nyToday()

  // The value can also change from outside (back/forward on a screen whose
  // state is the URL): a custom one is shown, a preset closes the inputs.
  const valueFrom = custom ? value.from : null
  const valueTo = custom ? value.to : null
  const valuePreset = custom ? null : value
  useEffect(() => {
    setError(null)
    if (!valueFrom) { setOpen(false); return }
    setOpen(true)
    setDraftFrom(valueFrom)
    setDraftTo(valueTo)
  }, [valueFrom, valueTo, valuePreset])

  const pickPreset = (id) => {
    setOpen(false)
    setError(null)
    if (value !== id) onChange(id)
  }

  const commit = (from, to) => {
    setDraftFrom(from)
    setDraftTo(to)
    if (!from || !to) { setError(null); return }
    const problem = rangeProblem(from, to, { max: today })
    setError(problem)
    if (!problem && (!custom || value.from !== from || value.to !== to)) onChange({ from, to })
  }

  const active = open ? 'custom' : value

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
      <div role="group" aria-label="Date range" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {presets.map((p) => (
          <RangeButton key={p.id} active={active === p.id} onClick={() => pickPreset(p.id)}>
            {p.label}
          </RangeButton>
        ))}
        {allowCustom && (
          <RangeButton active={active === 'custom'} onClick={() => setOpen(true)}>
            <CalendarRange size={12} aria-hidden="true" /> Custom
          </RangeButton>
        )}
      </div>
      {allowCustom && open && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <label style={labelStyle}>
            From
            <input
              type="date"
              aria-label="From"
              value={draftFrom}
              max={draftTo || today}
              onChange={(e) => commit(e.target.value, draftTo)}
              style={{ ...dateInputStyle, borderColor: error ? 'var(--red)' : 'var(--border)' }}
            />
          </label>
          <label style={labelStyle}>
            To
            <input
              type="date"
              aria-label="To"
              value={draftTo}
              min={draftFrom || undefined}
              max={today}
              onChange={(e) => commit(draftFrom, e.target.value)}
              style={{ ...dateInputStyle, borderColor: error ? 'var(--red)' : 'var(--border)' }}
            />
          </label>
        </div>
      )}
      {allowCustom && open && error && (
        <span role="alert" style={{ fontSize: 11, color: 'var(--red)', fontWeight: 600 }}>{error}</span>
      )}
    </div>
  )
}

function RangeButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
        background: active ? 'var(--accent-soft)' : 'transparent',
        color: active ? 'var(--accent)' : 'var(--text-muted)',
        border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
        transition: 'all .12s',
      }}
    >
      {children}
    </button>
  )
}

const labelStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
}

const dateInputStyle = {
  padding: '5px 8px', fontSize: 12, borderRadius: 6,
  border: '1px solid var(--border)', background: 'var(--bg-surface)',
  color: 'var(--text-primary)', fontFamily: 'ui-monospace, monospace',
}
